/**
 * REWORKED Live Events - Vendor Dashboard
 * PIN protection, auto-sync via Worker/Shopify, order queue, prep view, stock management
 */

class LiveDashboard {
  constructor(opts) {
    this.configUrl = opts.configUrl;
    this.workerUrl = opts.workerUrl;
    this.config = null;
    this.pin = '';
    this.orders = [];
    this.localStatus = {}; // shopify_id -> { status, size }
    this.stock = {};
    this.stockInitial = {};
    this.pollTimer = null;
    this.init();
  }

  async init() {
    try {
      const resp = await fetch(this.configUrl);
      this.config = await resp.json();
      this.loadLocalStatus();
      this.loadStock();
      this.populateManualForm();
    } catch (e) {
      console.error('Config load failed:', e);
    }
  }

  // ── PIN ──────────────────────────────────────────────────
  enterPin(num) {
    if (this.pin.length >= 4) return;
    this.pin += num;
    this.updatePinDots();

    if (this.pin.length === 4) {
      setTimeout(() => {
        if (this.pin === this.config.event.pin) {
          this.showScreen('dashboard');
          this.fetchOrders();
          this.startPolling();
          this.toast('DASHBOARD DÉBLOQUÉ');
        } else {
          document.querySelectorAll('.pin-dot').forEach(d => {
            d.classList.remove('filled');
            d.classList.add('error');
          });
          setTimeout(() => {
            this.pin = '';
            document.querySelectorAll('.pin-dot').forEach(d => d.classList.remove('error'));
          }, 500);
        }
      }, 150);
    }
  }

  backspacePin() {
    this.pin = this.pin.slice(0, -1);
    this.updatePinDots();
  }

  updatePinDots() {
    document.querySelectorAll('.pin-dot').forEach((d, i) => {
      d.classList.toggle('filled', i < this.pin.length);
    });
  }

  lock() {
    this.pin = '';
    this.updatePinDots();
    this.stopPolling();
    this.showScreen('pin');
  }

  showScreen(id) {
    document.querySelectorAll('.screen').forEach(s => s.classList.remove('active'));
    document.getElementById('scr-' + id).classList.add('active');
  }

  // ── POLLING SHOPIFY ORDERS ──────────────────────────────
  startPolling() {
    this.stopPolling();
    this.pollTimer = setInterval(() => this.fetchOrders(), 5000);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  async fetchOrders() {
    try {
      const resp = await fetch(this.workerUrl + '/orders?tag=live-event');
      const data = await resp.json();

      if (!data.orders) return;

      const prevCount = this.orders.length;

      // Merge draft orders - status from Shopify tags (synced across devices)
      this.orders = data.orders.map(o => {
        const props = o.line_item?.properties || {};
        const tags = (o.tags || '').toLowerCase();

        // Derive status from Shopify tags (source of truth, synced across devices)
        let status = 'pending';
        if (tags.includes('done')) status = 'completed';
        else if (tags.includes('cancelled')) status = 'cancelled';
        else if (tags.includes('preparing')) status = 'processing';

        return {
          id: o.name,
          draft_id: o.draft_id,
          event: this.config.event.id,
          event_name: this.config.event.name,
          timestamp: o.created_at,
          customer: {
            name: props['Client'] || ((o.customer?.firstName || '') + ' ' + (o.customer?.lastName || '')).trim() || 'Client',
            email: props['Email'] || o.customer?.email || '',
            phone: props['Telephone'] || '',
            size: props['Taille'] || '',
          },
          designs: {
            base_name: props['Base'] || 'Blanc',
            front_name: props['Design Avant'] || 'Aucun',
            back_name: props['Design Arriere'] || 'Aucun',
            stamps_text: props['Tampons'] || 'Aucun',
            stamps_data: (() => { try { return JSON.parse(props['_stamps_data'] || '[]'); } catch(e) { return []; } })(),
          },
          price: parseFloat(o.total_price) || 0,
          currency: 'EUR',
          payment_method: props['Paiement'] || '?',
          promo_code: props['Code promo'] || null,
          shopify_status: o.status,
          status: status,
        };
      });

      // Notify if new orders
      if (this.orders.length > prevCount && prevCount > 0) {
        const newCount = this.orders.length - prevCount;
        this.showNotif(newCount + ' NOUVELLE' + (newCount > 1 ? 'S' : '') + ' COMMANDE' + (newCount > 1 ? 'S' : ''));
      }

      this.renderAll();
    } catch (e) {
      console.warn('Poll error:', e);
    }
  }

  // ── LOCAL STATUS STORAGE ────────────────────────────────
  loadLocalStatus() {
    const stored = localStorage.getItem('rw_status_' + this.config.event.id);
    this.localStatus = stored ? JSON.parse(stored) : {};
  }

  saveLocalStatus() {
    localStorage.setItem('rw_status_' + this.config.event.id, JSON.stringify(this.localStatus));
  }

  updateOrderStatus(draftId, newStatus) {
    const order = this.orders.find(o => o.draft_id == draftId);
    const oldStatus = order?.status;

    if (order) {
      order.status = newStatus;
      const size = order.customer?.size;
      const base = order.designs?.base_name?.toLowerCase().includes('noir') ? 'noir' : 'blanc';

      // Decrement stock when validating payment (processing)
      if (newStatus === 'processing' && oldStatus === 'pending' && size) {
        if (this.stock[base] && this.stock[base][size] > 0) {
          this.stock[base][size]--;
          this.saveStock();
        }
      }

      // Restore stock when reverting to pending
      if (newStatus === 'pending' && (oldStatus === 'processing' || oldStatus === 'completed') && size) {
        if (this.stock[base]) {
          this.stock[base][size] = (this.stock[base][size] || 0) + 1;
          this.saveStock();
        }
      }
    }

    // Update Shopify draft tags (source of truth, synced across all devices)
    const tagMap = { pending: 'live-event', processing: 'live-event,preparing', completed: 'live-event,done', cancelled: 'live-event,cancelled' };
    fetch(this.workerUrl + '/order/' + draftId, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tags: tagMap[newStatus] || 'live-event' }),
    }).catch(e => console.warn('Status update error:', e));

    this.renderAll();
    const msgs = { pending: 'REMISE EN ATTENTE', processing: 'PAIEMENT VALIDÉ', completed: 'TERMINÉE', cancelled: 'ANNULÉE' };
    this.toast((order ? order.id : draftId) + ' ' + (msgs[newStatus] || newStatus));
  }

  // ── STOCK (per base color + size) ────────────────────────
  loadStock() {
    const stored = localStorage.getItem('rw_stock_v2_' + this.config.event.id);
    if (stored) {
      this.stock = JSON.parse(stored);
    } else {
      this.stock = JSON.parse(JSON.stringify(this.config.event.stock));
    }
    this.stockInitial = JSON.parse(JSON.stringify(this.config.event.stock));
  }

  saveStock() {
    localStorage.setItem('rw_stock_v2_' + this.config.event.id, JSON.stringify(this.stock));
  }


  adjustStock(base, size, delta) {
    if (!this.stock[base]) return;
    this.stock[base][size] = Math.max(0, (this.stock[base][size] || 0) + delta);
    this.saveStock();
    this.renderStock();
  }

  resetStock() {
    this.stock = JSON.parse(JSON.stringify(this.config.event.stock));
    this.saveStock();
    this.renderStock();
    this.toast('Stock réinitialisé');
  }

  getStockForSize(size) {
    return (this.stock.blanc?.[size] || 0) + (this.stock.noir?.[size] || 0);
  }

  // ── SEARCH ───────────────────────────────────────────────
  onSearch() {
    this.renderOrders();
    this.renderPrep();
    this.renderDone();
    this.renderCancelled();
    setTimeout(() => this.renderDashStamps(), 50);
  }

  _matchesSearch(o) {
    const q = (document.getElementById('dashSearch')?.value || '').toLowerCase().trim();
    if (!q) return true;
    const haystack = [
      o.id, o.customer?.name, o.customer?.email, o.customer?.phone,
      o.customer?.size, o.designs?.base_name, o.designs?.front_name,
      o.designs?.back_name, o.designs?.stamps_text, o.payment_method,
      String(o.price), o.promo_code
    ].filter(Boolean).join(' ').toLowerCase();
    return q.split(/\s+/).every(word => haystack.includes(word));
  }

  // ── RENDER ALL ──────────────────────────────────────────
  renderAll() {
    this.renderStats();
    this.renderOrders();
    this.renderPrep();
    this.renderStock();
    this.renderDone();
    this.renderCancelled();
    // Overlay stamps on photos after DOM is ready
    setTimeout(() => this.renderDashStamps(), 50);
  }

  // ── STATS ───────────────────────────────────────────────
  renderStats() {
    const pending = this.orders.filter(o => o.status === 'pending').length;
    const processing = this.orders.filter(o => o.status === 'processing').length;
    const completed = this.orders.filter(o => o.status === 'completed').length;
    const cancelled = this.orders.filter(o => o.status === 'cancelled').length;
    const totalCA = this.orders
      .filter(o => o.status !== 'cancelled')
      .reduce((sum, o) => sum + (o.price || 0), 0);

    document.getElementById('dashStats').innerHTML = `
      <div class="stat-cell"><div class="stat-num">${pending}</div><div class="stat-label">En attente</div></div>
      <div class="stat-cell"><div class="stat-num">${processing}</div><div class="stat-label">En préparation</div></div>
      <div class="stat-cell"><div class="stat-num">${completed}</div><div class="stat-label">Terminées</div></div>
      <div class="stat-cell"><div class="stat-num">${totalCA} EUR</div><div class="stat-label">CA total</div></div>
    `;

    document.getElementById('ordersBadge').textContent = pending + processing;
    document.getElementById('doneBadge').textContent = completed;
    document.getElementById('cancelledBadge').textContent = cancelled;
    document.getElementById('dashEventName').textContent = this.config.event.name;
  }

  // ── ORDERS LIST ─────────────────────────────────────────
  renderOrders() {
    const list = document.getElementById('ordersList');
    const empty = document.getElementById('ordersEmpty');

    const activeOrders = this.orders.filter(o => o.status !== 'cancelled' && o.status !== 'completed').filter(o => this._matchesSearch(o));

    if (activeOrders.length === 0) {
      list.innerHTML = '';
      empty.style.display = 'block';
      return;
    }

    empty.style.display = 'none';
    list.innerHTML = activeOrders.map(o => {
      const statusLabel = { pending: 'En attente', processing: 'En préparation', completed: 'Terminé', cancelled: 'Annulé' };
      const statusCls = { pending: 'pending', processing: 'processing', completed: 'completed', cancelled: 'cancelled' };

      return `
        <div class="order-card ${o.status === 'completed' ? 'done' : ''}">
          <div class="order-top">
            <div>
              <span class="order-id">${o.id}</span>
              <span class="order-time" style="margin-left:8px">${this.formatTime(o.timestamp)}</span>
            </div>
            <span class="bdg bdg-${statusCls[o.status] || 'pending'}">${statusLabel[o.status] || o.status}</span>
          </div>
          <div class="order-body">
            <div class="order-info">
              <h4>Client</h4>
              <p style="font-size:14px;font-weight:700">${o.customer?.name || 'Client'}</p>
              <p style="font-size:10px;color:var(--66);margin-top:2px">${o.customer?.email || ''}</p>
              ${o.customer?.phone ? '<p style="font-size:10px;color:var(--66)">' + o.customer.phone + '</p>' : ''}
              <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:6px">
                <span style="font-size:9px;padding:3px 8px;background:var(--f5);border-radius:10px;font-weight:600">Taille ${o.customer?.size || '?'}</span>
                <span style="font-size:9px;padding:3px 8px;background:var(--f5);border-radius:10px;font-weight:600">Base ${o.designs?.base_name || '?'}</span>
                <span style="font-size:9px;padding:3px 8px;background:var(--f5);border-radius:10px;font-weight:600">${o.payment_method || '?'}</span>
                ${o.promo_code && o.promo_code !== 'Aucun' ? '<span style="font-size:9px;padding:3px 8px;background:#e8f5e9;border-radius:10px;font-weight:600;color:#2e7d32">PROMO ' + o.promo_code + '</span>' : ''}
              </div>
            </div>
            <div class="order-info" style="flex:1">
              <h4>Produit</h4>
              <div class="order-designs-preview">
                <div class="order-design-box" style="position:relative;overflow:visible" onclick="dash.showFullView(dash.orders.find(x=>x.draft_id==${o.draft_id}))" data-order-front="${o.draft_id}">
                  <img src="${this.getMockupUrl('front', o.designs?.base_name, o.designs?.front_name)}">
                  <div class="order-zoom">&#x1F50D;</div>
                  AV: ${o.designs?.front_name || 'Aucun'}
                </div>
                <div class="order-design-box" style="position:relative;overflow:visible" onclick="dash.showFullView(dash.orders.find(x=>x.draft_id==${o.draft_id}))" data-order-back="${o.draft_id}">
                  <img src="${this.getMockupUrl('back', o.designs?.base_name, o.designs?.back_name)}">
                  <div class="order-zoom">&#x1F50D;</div>
                  AR: ${o.designs?.back_name || 'Aucun'}
                </div>
              </div>
              ${o.designs?.stamps_text && o.designs.stamps_text !== 'Aucun' ? '<div style="font-size:10px;margin-top:6px;padding:6px 8px;background:#fff3cd;border-radius:6px;font-weight:600">TAMPONS : ' + o.designs.stamps_text + '</div>' : ''}
              <div style="display:flex;align-items:center;gap:10px;margin-top:8px">
                <div style="font-size:16px;font-weight:700">${o.price || 0} EUR</div>
                <button class="btn btn-s" onclick="event.stopPropagation();dash.showFullView(dash.orders.find(x=>x.draft_id==${o.draft_id}))">Vue complete</button>
              </div>
            </div>
            <div class="order-actions">
              ${o.status === 'pending' ? `
                <div style="display:flex;gap:4px;align-items:center;flex-wrap:wrap">
                  <button class="btn btn-s btn-p" onclick="dash.updateOrderStatus(${o.draft_id},'processing')">Valider le paiement</button>
                  <button class="btn btn-s btn-d" onclick="dash.updateOrderStatus(${o.draft_id},'cancelled')">&times;</button>
                </div>
              ` : o.status === 'processing' ? `
                <button class="btn btn-s btn-g" onclick="dash.updateOrderStatus(${o.draft_id},'completed')">Terminer</button>
              ` : ''}
            </div>
          </div>
        </div>
      `;
    }).join('');
  }

  // ── PREPARATION VIEW ────────────────────────────────────
  renderPrep() {
    const prep = this.orders.filter(o => o.status === 'pending' || o.status === 'processing').filter(o => this._matchesSearch(o));
    document.getElementById('prepCount').textContent = prep.length + ' en attente';

    document.getElementById('prepList').innerHTML = prep.map(o => {
      const fName = o.designs?.front_name || 'N/A';
      const bName = o.designs?.back_name || 'N/A';
      const hasStamp = o.designs?.stampFront || o.designs?.stampBack;
      const stampDetail = [];
      if (o.designs?.stampFront) stampDetail.push('Avant');
      if (o.designs?.stampBack) stampDetail.push('Arriere');
      const size = o.customer?.size || '?';
      const name = o.customer?.name || 'Client';

      return `
        <div class="prep-card">
          <div class="prep-header">
            <div>
              <div class="prep-id">${o.id} <span class="bdg bdg-${o.status === 'pending' ? 'pending' : 'processing'}" style="margin-left:6px">${o.status === 'pending' ? 'En attente' : 'En prep'}</span></div>
              <div class="prep-client">${name} - Taille ${size} - ${o.payment_method || '?'}</div>
            </div>
            <div style="font-size:16px;font-weight:700">${o.price || 0} EUR</div>
          </div>
          <div class="order-designs-preview" style="margin:10px 0">
            <div class="order-design-box" style="position:relative;overflow:visible" onclick="dash.showFullView(dash.orders.find(x=>x.draft_id==${o.draft_id}))" data-prep-front="${o.draft_id}">
              <img src="${this.getMockupUrl('front', o.designs?.base_name, o.designs?.front_name)}">
              <div class="order-zoom">&#x1F50D;</div>
              AV: ${fName}
            </div>
            <div class="order-design-box" style="position:relative;overflow:visible" onclick="dash.showFullView(dash.orders.find(x=>x.draft_id==${o.draft_id}))" data-prep-back="${o.draft_id}">
              <img src="${this.getMockupUrl('back', o.designs?.base_name, o.designs?.back_name)}">
              <div class="order-zoom">&#x1F50D;</div>
              AR: ${bName}
            </div>
          </div>
          ${o.designs?.stamps_text && o.designs.stamps_text !== 'Aucun' ? '<div style="font-size:10px;padding:6px 8px;background:#fff3cd;border-radius:6px;font-weight:600;margin-bottom:8px">TAMPONS : ' + o.designs.stamps_text + '</div>' : ''}
          <div style="display:flex;gap:4px;align-items:center">
            ${o.status === 'pending' ? `
              <button class="btn btn-s btn-p" onclick="dash.updateOrderStatus(${o.draft_id},'processing')">Valider le paiement</button>
            ` : `<button class="btn btn-s btn-g" onclick="dash.updateOrderStatus(${o.draft_id},'completed')">Marquer termin\u00e9</button>`}
            <button class="btn btn-s" onclick="dash.showFullView(dash.orders.find(x=>x.draft_id==${o.draft_id}))">Vue compl\u00e8te</button>
          </div>
        </div>
      `;
    }).join('') || '<div style="text-align:center;padding:40px;color:var(--99)">Aucune commande en attente de préparation.</div>';
  }

  // ── DONE (completed orders) ─────────────────────────────
  renderDone() {
    const done = this.orders.filter(o => o.status === 'completed').filter(o => this._matchesSearch(o));
    const list = document.getElementById('doneList');
    const empty = document.getElementById('doneEmpty');
    const badge = document.getElementById('doneBadge');
    if (badge) badge.textContent = done.length;
    if (!list) return;
    if (done.length === 0) { list.innerHTML = ''; if (empty) empty.style.display = 'block'; return; }
    if (empty) empty.style.display = 'none';

    list.innerHTML = done.map(o => `
      <div class="order-card done">
        <div class="order-top">
          <span class="order-id">${o.id}</span>
          <span class="order-time" style="margin-left:8px">${this.formatTime(o.timestamp)}</span>
          <span class="bdg bdg-completed" style="margin-left:auto">Terminée</span>
        </div>
        <div class="order-body">
          <div class="order-info">
            <p style="font-size:14px;font-weight:700">${o.customer?.name || 'Client'}</p>
            <p style="font-size:10px;color:var(--66)">${o.customer?.email || ''} ${o.customer?.phone ? '| ' + o.customer.phone : ''}</p>
            <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:4px">
              <span style="font-size:9px;padding:3px 8px;background:var(--f5);border-radius:10px;font-weight:600">Taille ${o.customer?.size || '?'}</span>
              <span style="font-size:9px;padding:3px 8px;background:var(--f5);border-radius:10px;font-weight:600">Base ${o.designs?.base_name || '?'}</span>
              <span style="font-size:9px;padding:3px 8px;background:var(--f5);border-radius:10px;font-weight:600">${o.payment_method || '?'}</span>
            </div>
          </div>
          <div class="order-info" style="flex:1">
            <div class="order-designs-preview">
              <div class="order-design-box" style="position:relative;overflow:visible" onclick="dash.showFullView(dash.orders.find(x=>x.draft_id==${o.draft_id}))">
                <img src="${this.getMockupUrl('front', o.designs?.base_name, o.designs?.front_name)}">
                AV: ${o.designs?.front_name || 'Aucun'}
              </div>
              <div class="order-design-box" style="position:relative;overflow:visible" onclick="dash.showFullView(dash.orders.find(x=>x.draft_id==${o.draft_id}))">
                <img src="${this.getMockupUrl('back', o.designs?.base_name, o.designs?.back_name)}">
                AR: ${o.designs?.back_name || 'Aucun'}
              </div>
            </div>
            ${o.designs?.stamps_text && o.designs.stamps_text !== 'Aucun' ? '<div style="font-size:10px;margin-top:6px;padding:6px 8px;background:#fff3cd;border-radius:6px;font-weight:600">TAMPONS : ' + o.designs.stamps_text + '</div>' : ''}
            <div style="display:flex;align-items:center;gap:10px;margin-top:6px">
              <div style="font-size:16px;font-weight:700">${o.price || 0} EUR</div>
              <button class="btn btn-s btn-ghost" onclick="dash.updateOrderStatus(${o.draft_id},'pending')">Remettre en attente</button>
            </div>
          </div>
        </div>
      </div>
    `).join('');
  }

  // ── CANCELLED ───────────────────────────────────────────
  renderCancelled() {
    const cancelled = this.orders.filter(o => o.status === 'cancelled').filter(o => this._matchesSearch(o));
    const list = document.getElementById('cancelledList');
    const empty = document.getElementById('cancelledEmpty');
    const badge = document.getElementById('cancelledBadge');
    if (badge) badge.textContent = cancelled.length;
    if (!list) return;
    if (cancelled.length === 0) { list.innerHTML = ''; if (empty) empty.style.display = 'block'; return; }
    if (empty) empty.style.display = 'none';

    list.innerHTML = cancelled.map(o => `
      <div class="order-card done" style="opacity:0.6">
        <div class="order-top">
          <span class="order-id">${o.id}</span>
          <span class="order-time" style="margin-left:8px">${this.formatTime(o.timestamp)}</span>
          <span class="bdg bdg-cancelled" style="margin-left:auto">Annulée</span>
        </div>
        <div class="order-body">
          <div class="order-info">
            <p style="font-weight:700">${o.customer?.name || 'Client'}</p>
            <p style="font-size:10px;color:var(--66)">${o.customer?.email || ''} ${o.customer?.phone ? '| ' + o.customer.phone : ''}</p>
            <p style="font-size:10px;color:var(--66)">Taille ${o.customer?.size || '?'} | Base ${o.designs?.base_name || '?'} | ${o.payment_method || '?'} | ${o.price || 0} EUR</p>
            <p style="font-size:10px;color:var(--66)">AV: ${o.designs?.front_name || 'Aucun'} | AR: ${o.designs?.back_name || 'Aucun'}</p>
            ${o.designs?.stamps_text && o.designs.stamps_text !== 'Aucun' ? '<p style="font-size:10px;color:var(--66)">Tampons: ' + o.designs.stamps_text + '</p>' : ''}
          </div>
        </div>
      </div>
    `).join('');
  }

  // ── STOCK ───────────────────────────────────────────────
  renderStock() {
    const sizes = this.config.event.sizes;
    const grid = document.getElementById('stockGrid');

    // Table layout
    let html = '<table style="width:100%;border-collapse:collapse;text-align:center">';

    // Header
    html += '<tr style="border-bottom:2px solid #000"><th style="padding:10px;text-align:left;font-size:11px;letter-spacing:0.06em"></th>';
    sizes.forEach(s => { html += `<th style="padding:10px;font-size:13px;font-weight:700">${s}</th>`; });
    html += '<th style="padding:10px;font-size:11px;font-weight:700;color:var(--66)">TOTAL</th></tr>';

    // Blanc row
    ['blanc', 'noir'].forEach(base => {
      const label = base === 'blanc' ? 'BLANC' : 'NOIR';
      const bg = base === 'blanc' ? '#fff' : '#1a1a1a';
      const fg = base === 'blanc' ? '#000' : '#fff';
      let rowTotal = 0;

      html += `<tr style="border-bottom:1px solid #eee"><td style="padding:12px 10px;text-align:left"><span style="padding:4px 10px;background:${bg};color:${fg};border-radius:6px;font-size:10px;font-weight:700;letter-spacing:0.06em;${base==='blanc'?'border:1px solid #ddd':''}">${label}</span></td>`;

      sizes.forEach(s => {
        const cur = this.stock[base]?.[s] ?? 0;
        rowTotal += cur;
        const color = cur <= 0 ? 'var(--red)' : cur <= 2 ? '#b8860b' : 'inherit';
        html += `<td style="padding:12px 6px">
          <div style="font-size:22px;font-weight:700;color:${color}">${cur}</div>
          <div style="display:flex;gap:4px;justify-content:center;margin-top:4px">
            <button style="width:28px;height:28px;border-radius:50%;border:1px solid #ddd;background:#fff;cursor:pointer;font-size:14px;font-weight:700" onclick="dash.adjustStock('${base}','${s}',-1)">-</button>
            <button style="width:28px;height:28px;border-radius:50%;border:1px solid #ddd;background:#fff;cursor:pointer;font-size:14px;font-weight:700" onclick="dash.adjustStock('${base}','${s}',1)">+</button>
          </div>
        </td>`;
      });

      html += `<td style="padding:12px;font-size:16px;font-weight:700;color:var(--66)">${rowTotal}</td></tr>`;
    });

    // Total row
    let grandTotal = 0;
    html += '<tr style="border-top:2px solid #000"><td style="padding:12px 10px;text-align:left;font-size:11px;font-weight:700;letter-spacing:0.06em">TOTAL</td>';
    sizes.forEach(s => {
      const total = (this.stock.blanc?.[s] || 0) + (this.stock.noir?.[s] || 0);
      grandTotal += total;
      html += `<td style="padding:12px;font-size:22px;font-weight:700;${total <= 2 ? 'color:var(--red)' : ''}">${total}</td>`;
    });
    html += `<td style="padding:12px;font-size:22px;font-weight:700">${grandTotal}</td></tr>`;

    html += '</table>';
    grid.innerHTML = html;
  }

  // ── TABS ────────────────────────────────────────────────
  switchTab(tab) {
    document.querySelectorAll('.dash-tab').forEach(t => t.classList.toggle('on', t.dataset.tab === tab));
    document.querySelectorAll('.tab-content').forEach(c => c.classList.toggle('on', c.id === 'tab-' + tab));
  }

  // ── CLIENTS ────────────────────────────────────────────
  _getClientsFromOrders() {
    const map = {};
    this.orders.forEach(o => {
      const name = (o.customer?.name || 'Sans nom').trim();
      if (name === 'Sans nom' || name === 'Client') return;
      const key = name.toUpperCase();
      if (!map[key]) {
        map[key] = {
          name: name,
          email: o.customer?.email || '',
          phone: o.customer?.phone || '',
          orders: [],
          totalCA: 0,
          sizes: {},
        };
      }
      if (o.customer?.email && !map[key].email) map[key].email = o.customer.email;
      if (o.customer?.phone && !map[key].phone) map[key].phone = o.customer.phone;
      map[key].orders.push(o);
      map[key].totalCA += o.price || 0;
      const sz = o.customer?.size || '';
      if (sz) map[key].sizes[sz] = (map[key].sizes[sz] || 0) + 1;
    });
    return Object.values(map).sort((a, b) => b.orders.length - a.orders.length);
  }

  renderClients() {
    const el = document.getElementById('clientsList');
    if (!el) return;
    const clients = this._getClientsFromOrders();
    const badge = document.getElementById('clientsBadge');
    if (badge) badge.textContent = clients.length;

    el.innerHTML = clients.length ? clients.map(cl => {
      const sizesStr = Object.entries(cl.sizes).map(([s, n]) => `${s}(${n})`).join(', ');
      const completed = cl.orders.filter(o => o.status === 'completed').length;
      const cancelled = cl.orders.filter(o => o.status === 'cancelled').length;

      return `<div class="client-card-live" data-name="${cl.name.toLowerCase()}" data-client-name="${cl.name.replace(/"/g, '&quot;')}">
        <div style="display:flex;justify-content:space-between;align-items:center">
          <div style="display:flex;align-items:center;gap:10px">
            <input type="checkbox" class="client-checkbox" data-client="${cl.name.replace(/"/g, '&quot;')}" onchange="dash.updateSelectionCount()" onclick="event.stopPropagation()" style="width:16px;height:16px;cursor:pointer;accent-color:#111">
            <div>
              <div style="font-size:13px;font-weight:700">${cl.name}</div>
              <div style="font-size:10px;color:var(--88);margin-top:2px">${[cl.email, cl.phone].filter(Boolean).join(' \u2014 ') || 'Pas de coordonn\u00E9es'}</div>
            </div>
          </div>
          <div style="display:flex;gap:6px;align-items:center">
            <span style="font-size:16px;font-weight:700">${cl.totalCA.toFixed(0)}\u00A0\u20AC</span>
            <button class="btn btn-s btn-ghost" onclick="event.stopPropagation();dash.exportClientData('${cl.name.replace(/'/g, "\\'")}')">Exporter</button>
          </div>
        </div>
        <div style="display:flex;gap:14px;margin-top:8px;font-size:10px;color:var(--66)">
          <span><strong>${cl.orders.length}</strong> commande${cl.orders.length > 1 ? 's' : ''}</span>
          <span><strong>${completed}</strong> termin\u00E9e${completed > 1 ? 's' : ''}</span>
          ${cancelled ? `<span style="color:var(--red)"><strong>${cancelled}</strong> annul\u00E9e${cancelled > 1 ? 's' : ''}</span>` : ''}
          <span>Tailles : ${sizesStr || 'N/A'}</span>
        </div>
        <div style="margin-top:8px;border-top:1px solid var(--ee);padding-top:8px">
          ${cl.orders.map(o => `<div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;font-size:10px">
            <div>
              <span style="font-family:monospace;font-weight:600;margin-right:6px">${o.id}</span>
              <span style="color:var(--66)">${o.customer?.size || ''} ${o.designs?.base_name || ''}</span>
              <span style="margin-left:6px;color:var(--88)">${o.designs?.front_name !== 'Aucun' ? o.designs.front_name : ''} ${o.designs?.back_name !== 'Aucun' ? o.designs.back_name : ''}</span>
            </div>
            <div style="display:flex;align-items:center;gap:8px">
              <span class="status-tag st-${o.status}" style="font-size:8px;padding:2px 6px">${{pending:'En attente',processing:'En cours',completed:'Termin\u00E9e',cancelled:'Annul\u00E9e'}[o.status] || o.status}</span>
              <span style="font-weight:600">${o.price}\u00A0\u20AC</span>
            </div>
          </div>`).join('')}
        </div>
      </div>`;
    }).join('') : '<div style="text-align:center;padding:40px;color:var(--99)">Aucun client. Les clients apparaissent quand des commandes sont pass\u00E9es.</div>';
  }

  filterClientCards(q) {
    const s = q.toLowerCase();
    document.querySelectorAll('.client-card-live').forEach(c => {
      c.style.display = c.dataset.name.includes(s) || c.textContent.toLowerCase().includes(s) ? '' : 'none';
    });
  }

  updateSelectionCount() {
    const checked = document.querySelectorAll('.client-checkbox:checked');
    const btn = document.getElementById('btnExportSelection');
    if (btn) {
      if (checked.length > 0) {
        btn.style.display = '';
        btn.textContent = `Exporter la s\u00E9lection (${checked.length})`;
      } else {
        btn.style.display = 'none';
      }
    }
  }

  toggleSelectAll() {
    const boxes = document.querySelectorAll('.client-checkbox');
    const allChecked = [...boxes].every(b => b.checked);
    boxes.forEach(b => b.checked = !allChecked);
    this.updateSelectionCount();
  }

  exportSelectedClients() {
    const checked = document.querySelectorAll('.client-checkbox:checked');
    if (!checked.length) { this.toast('Aucun client s\u00E9lectionn\u00E9'); return; }

    const clients = this._getClientsFromOrders();
    const selectedNames = [...checked].map(cb => cb.dataset.client);

    // Export CSV des clients s\u00E9lectionn\u00E9s
    const rows = [['Nom','Email','T\u00E9l\u00E9phone','Commandes','Termin\u00E9es','CA Total','Tailles']];
    selectedNames.forEach(name => {
      const cl = clients.find(c => c.name === name);
      if (!cl) return;
      const completed = cl.orders.filter(o => o.status === 'completed').length;
      const sizesStr = Object.entries(cl.sizes).map(([s, n]) => s + '(' + n + ')').join(' ');
      rows.push([cl.name, cl.email, cl.phone, cl.orders.length, completed, cl.totalCA.toFixed(2) + ' \u20AC', sizesStr]);
    });
    const csv = rows.map(r => r.map(c => '"' + String(c || '').replace(/"/g, '""') + '"').join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'clients-selection-' + (this.config?.event?.id || 'reworked') + '.csv';
    a.click(); URL.revokeObjectURL(a.href);
    this.toast(`${selectedNames.length} client${selectedNames.length > 1 ? 's' : ''} export\u00E9${selectedNames.length > 1 ? 's' : ''}`);
  }

  exportClientData(clientName) {
    const clients = this._getClientsFromOrders();
    const cl = clients.find(c => c.name === clientName);
    if (!cl) { this.toast('Client introuvable'); return; }

    const completed = cl.orders.filter(o => o.status === 'completed');
    const caCompleted = completed.reduce((s, o) => s + (o.price || 0), 0);
    const sizesStr = Object.entries(cl.sizes).map(([s, n]) => `${s} (\u00D7${n})`).join(', ');
    const eventName = this.config?.event?.name || 'REWORKED Live';

    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Fiche client \u2014 ${cl.name}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Inter',-apple-system,sans-serif;font-size:10px;line-height:1.5;color:#111;padding:30px 35px;max-width:210mm;margin:0 auto}
h1{font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:2px}
.sub{font-size:9px;color:#666;margin-bottom:20px}
.info-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:18px}
.info-box{background:#f7f7f7;padding:10px 14px;border-radius:4px}
.info-label{font-size:7px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:3px}
.info-value{font-size:11px;font-weight:600}
.stats-row{display:flex;gap:12px;margin-bottom:18px}
.stat-box{flex:1;background:#f7f7f7;padding:12px;text-align:center;border-radius:4px}
.stat-num{font-size:20px;font-weight:700}
.stat-label{font-size:7px;text-transform:uppercase;letter-spacing:0.5px;color:#888;margin-top:2px}
.section-title{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;padding-bottom:6px;border-bottom:1px solid #ddd;margin-bottom:10px}
table{width:100%;border-collapse:collapse}
th{background:#111;color:#fff;font-size:7px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;padding:6px 8px;text-align:left}
th:last-child{text-align:right}
td{padding:6px 8px;border-bottom:1px solid #eee;font-size:9px}
td:last-child{text-align:right;font-weight:500}
.st{display:inline-block;font-size:7px;font-weight:600;padding:2px 6px;border-radius:3px;text-transform:uppercase}
.st-completed{background:#e8f5e9;color:#2e7d32}
.st-cancelled{background:#fff5f5;color:#c62828}
.st-pending{background:#f0f7ff;color:#0066cc}
.st-processing{background:#fff3cd;color:#d97706}
.footer{margin-top:25px;padding-top:10px;border-top:1px solid #ddd;font-size:7px;color:#888;text-align:center}
</style></head><body>

<h1>${cl.name}</h1>
<div class="sub">Fiche client \u2014 ${eventName} \u2014 Export\u00E9e le ${new Date().toLocaleDateString('fr-FR')}</div>

<div class="info-grid">
  <div class="info-box"><div class="info-label">Email</div><div class="info-value">${cl.email || 'Non renseign\u00E9'}</div></div>
  <div class="info-box"><div class="info-label">T\u00E9l\u00E9phone</div><div class="info-value">${cl.phone || 'Non renseign\u00E9'}</div></div>
  <div class="info-box"><div class="info-label">Tailles command\u00E9es</div><div class="info-value">${sizesStr || 'N/A'}</div></div>
</div>

<div class="stats-row">
  <div class="stat-box"><div class="stat-num">${cl.orders.length}</div><div class="stat-label">Commandes</div></div>
  <div class="stat-box"><div class="stat-num">${completed.length}</div><div class="stat-label">Termin\u00E9es</div></div>
  <div class="stat-box"><div class="stat-num">${cl.totalCA.toFixed(0)} \u20AC</div><div class="stat-label">CA total</div></div>
  <div class="stat-box"><div class="stat-num">${caCompleted.toFixed(0)} \u20AC</div><div class="stat-label">CA encaiss\u00E9</div></div>
</div>

<h3 class="section-title">D\u00E9tail des commandes</h3>
<table>
  <thead><tr><th>N\u00B0</th><th>Date</th><th>Taille</th><th>Base</th><th>Design avant</th><th>Design arri\u00E8re</th><th>Paiement</th><th>Statut</th><th>Montant</th></tr></thead>
  <tbody>
    ${cl.orders.map(o => `<tr>
      <td style="font-family:monospace;font-weight:600">${o.id}</td>
      <td>${o.timestamp ? new Date(o.timestamp).toLocaleDateString('fr-FR') : ''}</td>
      <td>${o.customer?.size || ''}</td>
      <td>${o.designs?.base_name || ''}</td>
      <td>${o.designs?.front_name || ''}</td>
      <td>${o.designs?.back_name || ''}</td>
      <td>${o.payment_method || ''}</td>
      <td><span class="st st-${o.status}">${{pending:'En attente',processing:'En cours',completed:'Termin\u00E9e',cancelled:'Annul\u00E9e'}[o.status] || o.status}</span></td>
      <td style="font-weight:600">${o.price}\u00A0\u20AC</td>
    </tr>`).join('')}
  </tbody>
</table>

<div class="footer">
  REWORKED \u2014 18 Rue Calliet, 69001 Lyon \u2014 atelier@rework-ed.com<br>
  ${eventName} \u2014 Donn\u00E9es au ${new Date().toLocaleDateString('fr-FR')}
</div>
</body></html>`;

    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    a.download = `fiche-client-${cl.name.toLowerCase().replace(/\\s+/g, '-')}.html`;
    a.click(); URL.revokeObjectURL(a.href);
    this.toast('Fiche ' + cl.name + ' export\u00E9e');
  }

  exportAllClients() {
    const clients = this._getClientsFromOrders();
    if (!clients.length) { this.toast('Aucun client'); return; }
    const rows = [['Nom','Email','T\u00E9l\u00E9phone','Commandes','Termin\u00E9es','CA Total','Tailles']];
    clients.forEach(cl => {
      const completed = cl.orders.filter(o => o.status === 'completed').length;
      const sizesStr = Object.entries(cl.sizes).map(([s, n]) => s + '(' + n + ')').join(' ');
      rows.push([cl.name, cl.email, cl.phone, cl.orders.length, completed, cl.totalCA.toFixed(2) + ' \u20AC', sizesStr]);
    });
    const csv = rows.map(r => r.map(c => '"' + String(c || '').replace(/"/g, '""') + '"').join(',')).join('\\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'clients-' + (this.config?.event?.id || 'reworked') + '.csv';
    a.click(); URL.revokeObjectURL(a.href);
    this.toast('CSV clients export\u00E9');
  }

  // ── MANUAL ENTRY ────────────────────────────────────────
  populateManualForm() {
    const sizeSelect = document.getElementById('manualSize');
    this.config.event.sizes.forEach(s => {
      const opt = document.createElement('option');
      opt.value = s; opt.textContent = s;
      sizeSelect.appendChild(opt);
    });

    const frontSelect = document.getElementById('manualFront');
    const backSelect = document.getElementById('manualBack');
    this.config.designs.forEach(d => {
      if (d.position === 'both' || d.position === 'front') {
        const opt = document.createElement('option');
        opt.value = d.id; opt.textContent = d.name;
        frontSelect.appendChild(opt);
      }
      if (d.position === 'both' || d.position === 'back') {
        const opt = document.createElement('option');
        opt.value = d.id; opt.textContent = d.name;
        backSelect.appendChild(opt);
      }
    });
  }

  openManualEntry() {
    document.getElementById('manualEntryModal').classList.add('on');
  }

  closeManualEntry() {
    document.getElementById('manualEntryModal').classList.remove('on');
  }

  async submitManual(e) {
    e.preventDefault();
    const name = document.getElementById('manualName').value;
    const size = document.getElementById('manualSize').value;
    const frontId = document.getElementById('manualFront').value;
    const backId = document.getElementById('manualBack').value;
    const stampF = document.getElementById('manualStampF').value === 'true';
    const stampB = document.getElementById('manualStampB').value === 'true';

    const frontDesign = this.config.designs.find(d => d.id === frontId);
    const backDesign = this.config.designs.find(d => d.id === backId);

    let count = 0;
    if (frontId) count++;
    if (backId) count++;
    if (stampF) count++;
    if (stampB) count++;
    const extras = count > 1 ? (count - 1) * this.config.event.extra_price : 0;
    const price = this.config.event.base_price + extras;

    const orderData = {
      event: this.config.event.id,
      event_name: this.config.event.name,
      customer: { firstName: name, lastName: '', email: 'manual@event.com', size: size },
      designs: {
        front_name: frontDesign ? frontDesign.name : 'Sans design',
        back_name: backDesign ? backDesign.name : 'Sans design',
        stampFront: stampF,
        stampBack: stampB,
      },
      price: price,
      currency: this.config.event.currency,
      payment_method: 'cash',
    };

    // Send to Worker to create Shopify order
    try {
      const resp = await fetch(this.workerUrl + '/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });
      const result = await resp.json();
      if (result.success) {
        this.toast('Commande ' + result.name + ' créée');
        this.fetchOrders(); // Refresh from Shopify
      } else {
        this.toast('Erreur: ' + (result.error || 'Réessayez'));
      }
    } catch (err) {
      this.toast('Erreur de connexion');
    }

    this.closeManualEntry();
    document.getElementById('manualForm').reset();
    return false;
  }

  // ── EXPORT (legacy, kept for orders tab) ─────────────────
  exportOrders() {
    this.switchTab('rapport');
    this.renderRapport();
  }

  // ── RAPPORT HELPERS ─────────────────────────────────────
  _isTestOrder(o) {
    const lc = (o.customer?.name || '').toLowerCase();
    return lc.includes('robyn') || lc.includes('test') || lc.startsWith('tu ');
  }

  getFilteredOrders() {
    const status = document.getElementById('rapportStatus')?.value || 'all';
    const limit = parseInt(document.getElementById('rapportLimit')?.value || '0');
    const sort = document.getElementById('rapportSort')?.value || 'date-desc';
    const excludeTests = document.getElementById('rapportExcludeTests')?.value !== 'no';

    let filtered = [...this.orders];

    // Status filter
    if (status === 'no-cancelled') filtered = filtered.filter(o => o.status !== 'cancelled');
    else if (status !== 'all') filtered = filtered.filter(o => o.status === status);

    // Exclude tests
    if (excludeTests) filtered = filtered.filter(o => !this._isTestOrder(o));

    // Sort
    filtered.sort((a, b) => {
      switch (sort) {
        case 'date-asc': return new Date(a.timestamp) - new Date(b.timestamp);
        case 'date-desc': return new Date(b.timestamp) - new Date(a.timestamp);
        case 'price-desc': return (b.price || 0) - (a.price || 0);
        case 'price-asc': return (a.price || 0) - (b.price || 0);
        case 'name-asc': return (a.customer?.name || '').localeCompare(b.customer?.name || '');
        default: return 0;
      }
    });

    // Limit
    if (limit > 0) filtered = filtered.slice(0, limit);

    return filtered;
  }

  // ── RENDER RAPPORT ──────────────────────────────────────
  renderRapport() {
    const orders = this.getFilteredOrders();

    // Stats
    const ca = orders.reduce((s, o) => s + (o.price || 0), 0);
    const avgPrice = orders.length ? (ca / orders.length).toFixed(2) : '0';
    const bySize = {};
    const byBase = {};
    const byDesign = {};
    const byPayment = {};
    let withStamps = 0;

    orders.forEach(o => {
      const sz = o.customer?.size || '?';
      bySize[sz] = (bySize[sz] || 0) + 1;

      const base = (o.designs?.base_name || '?').replace('T-shirt ', '');
      byBase[base] = (byBase[base] || 0) + 1;

      const front = o.designs?.front_name || 'Aucun';
      if (front !== 'Aucun') byDesign[front] = (byDesign[front] || 0) + 1;

      const pay = o.payment_method || '?';
      byPayment[pay] = (byPayment[pay] || 0) + 1;

      if (o.designs?.stamps_text && o.designs.stamps_text !== 'Aucun') withStamps++;
    });

    const sizeStr = ['XS','S','M','L','XL'].map(s => `${s}:${bySize[s]||0}`).join(' / ');
    const baseStr = Object.entries(byBase).map(([k,v]) => `${k}:${v}`).join(' / ');
    const designStr = Object.entries(byDesign).sort((a,b) => b[1]-a[1]).map(([k,v]) => `${k}:${v}`).join(' / ');
    const payStr = Object.entries(byPayment).map(([k,v]) => `${k}:${v}`).join(' / ');

    document.getElementById('rapportStats').innerHTML = `
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:10px">
        <div style="background:var(--f5);padding:14px;border-radius:10px">
          <div style="font-size:24px;font-weight:700">${orders.length}</div>
          <div style="font-size:9px;letter-spacing:0.06em;color:var(--66);margin-top:2px">COMMANDES</div>
        </div>
        <div style="background:var(--f5);padding:14px;border-radius:10px">
          <div style="font-size:24px;font-weight:700">${ca.toFixed(0)} EUR</div>
          <div style="font-size:9px;letter-spacing:0.06em;color:var(--66);margin-top:2px">CA TOTAL</div>
        </div>
        <div style="background:var(--f5);padding:14px;border-radius:10px">
          <div style="font-size:24px;font-weight:700">${avgPrice} EUR</div>
          <div style="font-size:9px;letter-spacing:0.06em;color:var(--66);margin-top:2px">PANIER MOYEN</div>
        </div>
        <div style="background:var(--f5);padding:14px;border-radius:10px">
          <div style="font-size:24px;font-weight:700">${withStamps}</div>
          <div style="font-size:9px;letter-spacing:0.06em;color:var(--66);margin-top:2px">AVEC TAMPONS</div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:8px;margin-top:10px;font-size:11px">
        <div style="padding:8px 12px;background:var(--f5);border-radius:8px"><strong>TAILLES</strong> ${sizeStr}</div>
        <div style="padding:8px 12px;background:var(--f5);border-radius:8px"><strong>BASES</strong> ${baseStr}</div>
        <div style="padding:8px 12px;background:var(--f5);border-radius:8px"><strong>PAIEMENT</strong> ${payStr}</div>
        ${designStr ? `<div style="padding:8px 12px;background:var(--f5);border-radius:8px"><strong>DESIGNS</strong> ${designStr}</div>` : ''}
      </div>
    `;

    // Table
    const statusLabels = { pending: 'En attente', processing: 'En prep', completed: 'Terminée', cancelled: 'Annulée' };
    let html = `<table id="rapportTableInner" style="width:100%;border-collapse:collapse;font-size:11px">
      <thead><tr style="border-bottom:2px solid #000;text-align:left">
        <th style="padding:8px 6px">#</th>
        <th style="padding:8px 6px">ID</th>
        <th style="padding:8px 6px">DATE</th>
        <th style="padding:8px 6px">CLIENT</th>
        <th style="padding:8px 6px">EMAIL</th>
        <th style="padding:8px 6px">TEL</th>
        <th style="padding:8px 6px">TAILLE</th>
        <th style="padding:8px 6px">BASE</th>
        <th style="padding:8px 6px">DESIGN AV</th>
        <th style="padding:8px 6px">DESIGN AR</th>
        <th style="padding:8px 6px">TAMPONS</th>
        <th style="padding:8px 6px">PRIX</th>
        <th style="padding:8px 6px">PAIEMENT</th>
        <th style="padding:8px 6px">PROMO</th>
        <th style="padding:8px 6px">STATUT</th>
      </tr></thead><tbody>`;

    orders.forEach((o, i) => {
      const d = new Date(o.timestamp);
      const dateStr = d.toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric' });
      const timeStr = d.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' });
      html += `<tr style="border-bottom:1px solid #eee${o.status === 'cancelled' ? ';opacity:0.5' : ''}">
        <td style="padding:6px">${i + 1}</td>
        <td style="padding:6px;font-weight:700">${o.id}</td>
        <td style="padding:6px;white-space:nowrap">${dateStr} ${timeStr}</td>
        <td style="padding:6px;font-weight:600">${o.customer?.name || ''}</td>
        <td style="padding:6px">${o.customer?.email || ''}</td>
        <td style="padding:6px">${o.customer?.phone || ''}</td>
        <td style="padding:6px;text-align:center;font-weight:700">${o.customer?.size || ''}</td>
        <td style="padding:6px">${(o.designs?.base_name || '').replace('T-shirt ', '')}</td>
        <td style="padding:6px">${o.designs?.front_name || 'Aucun'}</td>
        <td style="padding:6px">${o.designs?.back_name || 'Aucun'}</td>
        <td style="padding:6px;font-size:10px">${o.designs?.stamps_text || 'Aucun'}</td>
        <td style="padding:6px;font-weight:700">${o.price || 0} EUR</td>
        <td style="padding:6px">${o.payment_method || '?'}</td>
        <td style="padding:6px">${o.promo_code && o.promo_code !== 'Aucun' ? o.promo_code : ''}</td>
        <td style="padding:6px"><span class="bdg bdg-${o.status}">${statusLabels[o.status] || o.status}</span></td>
      </tr>`;
    });

    html += `</tbody><tfoot><tr style="border-top:2px solid #000;font-weight:700">
      <td colspan="11" style="padding:8px 6px">TOTAL : ${orders.length} commandes</td>
      <td style="padding:8px 6px">${ca.toFixed(0)} EUR</td>
      <td colspan="3"></td>
    </tr></tfoot></table>`;

    document.getElementById('rapportTable').innerHTML = html;
  }

  // ── BUILD DATA ROWS (shared by all exports) ─────────────
  _buildExportRows(orders) {
    const headers = ['#', 'ID', 'Date', 'Heure', 'Client', 'Email', 'Telephone', 'Taille', 'Base', 'Design Avant', 'Design Arriere', 'Tampons', 'Prix (EUR)', 'Paiement', 'Code Promo', 'Statut'];
    const statusLabels = { pending: 'En attente', processing: 'En préparation', completed: 'Terminée', cancelled: 'Annulée' };

    const rows = orders.map((o, i) => {
      const d = new Date(o.timestamp);
      return [
        i + 1,
        o.id || '',
        d.toLocaleDateString('fr-FR'),
        d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        o.customer?.name || '',
        o.customer?.email || '',
        o.customer?.phone || '',
        o.customer?.size || '',
        (o.designs?.base_name || '').replace('T-shirt ', ''),
        o.designs?.front_name || 'Aucun',
        o.designs?.back_name || 'Aucun',
        o.designs?.stamps_text || 'Aucun',
        o.price || 0,
        o.payment_method || '',
        (o.promo_code && o.promo_code !== 'Aucun') ? o.promo_code : '',
        statusLabels[o.status] || o.status,
      ];
    });

    return { headers, rows };
  }

  // ── CSV EXPORT ──────────────────────────────────────────
  exportRapportCSV() {
    const orders = this.getFilteredOrders();
    if (!orders.length) { this.toast('Aucune commande'); return; }

    const { headers, rows } = this._buildExportRows(orders);

    const escCSV = (v) => {
      const s = String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? '"' + s.replace(/"/g, '""') + '"' : s;
    };

    const csv = '\uFEFF' + [headers.map(escCSV).join(','), ...rows.map(r => r.map(escCSV).join(','))].join('\n');

    this._downloadFile(csv, 'text/csv;charset=utf-8', `rapport-${this.config.event.id}-${this._dateSlug()}.csv`);
    this.toast('CSV téléchargé');
  }

  // ── EXCEL EXPORT (SpreadsheetML XML) ────────────────────
  exportRapportExcel() {
    const orders = this.getFilteredOrders();
    if (!orders.length) { this.toast('Aucune commande'); return; }

    const { headers, rows } = this._buildExportRows(orders);
    const ca = orders.reduce((s, o) => s + (o.price || 0), 0);
    const eventName = this.config.event.name || 'Event';

    const escXml = (v) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    let xmlRows = '';

    // Header row
    xmlRows += '<Row ss:StyleID="Header">';
    headers.forEach(h => { xmlRows += `<Cell><Data ss:Type="String">${escXml(h)}</Data></Cell>`; });
    xmlRows += '</Row>';

    // Data rows
    rows.forEach(r => {
      xmlRows += '<Row>';
      r.forEach((v, ci) => {
        const isNum = ci === 0 || ci === 12; // # and Prix
        if (isNum) {
          xmlRows += `<Cell><Data ss:Type="Number">${v}</Data></Cell>`;
        } else {
          xmlRows += `<Cell><Data ss:Type="String">${escXml(v)}</Data></Cell>`;
        }
      });
      xmlRows += '</Row>';
    });

    // Total row
    xmlRows += '<Row ss:StyleID="Header">';
    xmlRows += `<Cell ss:MergeAcross="11"><Data ss:Type="String">TOTAL : ${orders.length} commandes</Data></Cell>`;
    xmlRows += `<Cell><Data ss:Type="Number">${ca}</Data></Cell>`;
    xmlRows += '<Cell ss:MergeAcross="2"><Data ss:Type="String"></Data></Cell>';
    xmlRows += '</Row>';

    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Styles>
  <Style ss:ID="Default"><Font ss:FontName="Arial" ss:Size="10"/></Style>
  <Style ss:ID="Header"><Font ss:FontName="Arial" ss:Size="10" ss:Bold="1"/><Interior ss:Color="#F2F2F2" ss:Pattern="Solid"/></Style>
 </Styles>
 <Worksheet ss:Name="${escXml(eventName)}">
  <Table>${xmlRows}</Table>
 </Worksheet>
</Workbook>`;

    this._downloadFile(xml, 'application/vnd.ms-excel', `rapport-${this.config.event.id}-${this._dateSlug()}.xls`);
    this.toast('Excel téléchargé');
  }

  // ── PDF SHARED STYLE (DA REWORKED) ───────────────────────
  _pdfOpen(title, docType, docNum, bodyContent) {
    const logoUrl = this.configUrl.substring(0, this.configUrl.lastIndexOf('/') + 1) + 'logo-reworked.svg';
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>${title}</title>
<style>
  @page { margin: 20mm 16mm; }
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 9px; color: #000; padding: 40px; line-height: 1.5; }

  /* ── Header ── */
  .rw-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:32px; }
  .rw-header-left img { height:28px; margin-bottom:8px; display:block; }
  .rw-header-left .rw-addr { font-size:8px; color:#666; line-height:1.6; }
  .rw-header-right { text-align:right; }
  .rw-header-right .rw-doctype { display:inline-block; font-size:8px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; border:1px solid #000; padding:4px 10px; margin-bottom:6px; }
  .rw-header-right .rw-docnum { font-size:13px; font-weight:700; letter-spacing:0.04em; }
  .rw-header-right .rw-docdate { font-size:8px; color:#666; margin-top:2px; }

  /* ── Info bar ── */
  .rw-infobar { background:#f7f7f7; border:1px solid #eee; border-radius:4px; padding:12px 16px; margin-bottom:24px; display:flex; gap:32px; flex-wrap:wrap; }
  .rw-infobar .ib-item { font-size:9px; }
  .rw-infobar .ib-item strong { font-weight:700; }

  /* ── Sections ── */
  .rw-section { margin-bottom:20px; }
  .rw-section-title { font-size:10px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; padding-bottom:6px; border-bottom:2px solid #000; margin-bottom:10px; }
  .rw-section-num { font-size:10px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; margin-bottom:12px; }

  /* ── Tables ── */
  table { width:100%; border-collapse:collapse; margin-bottom:4px; }
  thead th { background:#3a3a3a; color:#fff; font-size:7.5px; font-weight:700; letter-spacing:0.06em; text-transform:uppercase; padding:7px 5px; text-align:left; }
  thead th.ar { text-align:right; }
  tbody td { padding:5px; border-bottom:1px solid #eee; font-size:8px; }
  tbody tr:nth-child(even) { background:#fafafa; }
  tbody td.ar { text-align:right; }
  tbody td.ac { text-align:center; }
  tbody td.bld { font-weight:700; }
  tfoot td { padding:7px 5px; font-weight:700; font-size:9px; }

  /* ── Recap rows ── */
  .rw-recap { border:1px solid #eee; border-radius:4px; overflow:hidden; margin-bottom:12px; }
  .rw-recap-row { display:flex; justify-content:space-between; padding:8px 14px; font-size:10px; border-bottom:1px solid #eee; }
  .rw-recap-row:last-child { border-bottom:none; }
  .rw-recap-row.sub { font-size:9px; color:#666; }
  .rw-recap-row.total { background:#f7f7f7; font-weight:700; font-size:11px; }
  .rw-recap-row.grand { background:#000; color:#fff; font-weight:700; font-size:13px; padding:12px 14px; }

  /* ── Stat boxes ── */
  .rw-stats { display:flex; gap:12px; margin-bottom:16px; }
  .rw-stat { flex:1; border:1px solid #eee; border-radius:4px; padding:12px 14px; }
  .rw-stat-val { font-size:18px; font-weight:700; letter-spacing:-0.02em; }
  .rw-stat-label { font-size:7.5px; color:#666; letter-spacing:0.06em; text-transform:uppercase; margin-top:2px; }

  /* ── Cols ── */
  .rw-cols { display:flex; gap:16px; margin-bottom:16px; }
  .rw-cols > div { flex:1; }

  /* ── Info box (left border accent) ── */
  .rw-note { border-left:3px solid #e8a020; background:#fffcf5; padding:10px 14px; font-size:8.5px; margin-bottom:12px; border-radius:0 4px 4px 0; }

  /* ── Footer ── */
  .rw-footer { margin-top:28px; padding-top:12px; border-top:1px solid #ddd; text-align:center; font-size:7.5px; color:#999; line-height:1.6; }

  @media print { body { padding:0; } .rw-footer { position:fixed; bottom:0; left:0; right:0; } }
</style></head><body>

  <div class="rw-header">
    <div class="rw-header-left">
      <img src="${logoUrl}" alt="REWORKED" onerror="this.outerHTML='<div style=\\'font-size:22px;font-weight:900;font-style:italic;letter-spacing:-0.02em\\'>REWORKED</div>'">
      <div class="rw-addr">18 Rue Calliet, 69001 Lyon<br>SIRET : 938 614 773 00019<br>06 01 42 71 70 &mdash; atelier@rework-ed.com<br>www.rework-ed.com</div>
    </div>
    <div class="rw-header-right">
      <div class="rw-doctype">${docType}</div><br>
      <div class="rw-docnum">${docNum}</div>
      <div class="rw-docdate">${dateStr}</div>
    </div>
  </div>

  ${bodyContent}

  <div class="rw-footer">
    REWORKED &mdash; Auto-entrepreneur, TVA non applicable (art. 293 B du CGI)<br>
    18 Rue Calliet, 69001 Lyon &mdash; 06 01 42 71 70 &mdash; atelier@rework-ed.com &mdash; www.rework-ed.com
  </div>
</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
    this.toast('Fenetre impression ouverte');
  }

  // ── PDF EXPORT RAPPORT ──────────────────────────────────
  exportRapportPDF() {
    const orders = this.getFilteredOrders();
    if (!orders.length) { this.toast('Aucune commande'); return; }

    const { headers, rows } = this._buildExportRows(orders);
    const ca = orders.reduce((s, o) => s + (o.price || 0), 0);
    const eventName = this.config.event.name || 'Event';
    const eventDate = this.config.event.date_display || '';
    const avg = orders.length ? (ca / orders.length).toFixed(0) : 0;

    const bySize = {}; const byBase = {}; let withStamps = 0;
    orders.forEach(o => {
      bySize[o.customer?.size || '?'] = (bySize[o.customer?.size || '?'] || 0) + 1;
      const b = (o.designs?.base_name || '?').replace('T-shirt ', '');
      byBase[b] = (byBase[b] || 0) + 1;
      if (o.designs?.stamps_text && o.designs.stamps_text !== 'Aucun') withStamps++;
    });

    let tableHtml = '<table><thead><tr>';
    headers.forEach((h, i) => { tableHtml += `<th${i === 12 ? ' class="ar"' : ''}>${h}</th>`; });
    tableHtml += '</tr></thead><tbody>';
    rows.forEach(r => {
      tableHtml += '<tr>';
      r.forEach((v, ci) => {
        const cls = ci === 12 ? ' class="ar bld"' : (ci === 0 || ci === 7) ? ' class="ac"' : '';
        tableHtml += `<td${cls}>${v}</td>`;
      });
      tableHtml += '</tr>';
    });
    tableHtml += `</tbody><tfoot><tr style="border-top:2px solid #000"><td colspan="12">TOTAL : ${orders.length} commandes</td><td class="ar">${ca.toFixed(0)} EUR</td><td colspan="3"></td></tr></tfoot></table>`;

    const sizeStr = ['XS','S','M','L','XL'].map(s => `${s}: ${bySize[s]||0}`).join(' &nbsp;&nbsp; ');
    const baseStr = Object.entries(byBase).map(([k,v]) => `${k}: ${v}`).join(' &nbsp;&nbsp; ');

    const body = `
      <div class="rw-infobar">
        <div class="ib-item"><strong>Date :</strong> ${eventDate}</div>
        <div class="ib-item"><strong>Lieu :</strong> Lyon</div>
        <div class="ib-item"><strong>Event :</strong> ${eventName}</div>
        <div class="ib-item"><strong>Commandes :</strong> ${orders.length}</div>
      </div>

      <div class="rw-stats">
        <div class="rw-stat"><div class="rw-stat-val">${orders.length}</div><div class="rw-stat-label">Commandes</div></div>
        <div class="rw-stat"><div class="rw-stat-val">${ca.toFixed(0)} &euro;</div><div class="rw-stat-label">CA Total</div></div>
        <div class="rw-stat"><div class="rw-stat-val">${avg} &euro;</div><div class="rw-stat-label">Panier moyen</div></div>
        <div class="rw-stat"><div class="rw-stat-val">${withStamps}</div><div class="rw-stat-label">Avec tampons</div></div>
      </div>

      <div class="rw-cols" style="margin-bottom:16px;font-size:9px">
        <div><strong>Tailles :</strong> ${sizeStr}</div>
        <div><strong>Bases :</strong> ${baseStr}</div>
      </div>

      <div class="rw-section">
        <div class="rw-section-title">Détail des commandes</div>
        ${tableHtml}
      </div>`;

    this._pdfOpen('Rapport ' + eventName, 'RAPPORT DE VENTES', eventName, body);
  }

  // ── CLIPBOARD (tab-separated, paste into Google Sheets) ──
  exportRapportClipboard() {
    const orders = this.getFilteredOrders();
    if (!orders.length) { this.toast('Aucune commande'); return; }

    const { headers, rows } = this._buildExportRows(orders);
    const tsv = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');

    navigator.clipboard.writeText(tsv).then(() => {
      this.toast('Copié ! Colle dans Google Sheets (Ctrl+V)');
    }).catch(() => {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = tsv;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
      this.toast('Copié ! Colle dans Google Sheets (Ctrl+V)');
    });
  }

  // ── DOWNLOAD HELPER ─────────────────────────────────────
  _downloadFile(content, type, filename) {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  _dateSlug() {
    return new Date().toISOString().split('T')[0];
  }

  // ══════════════════════════════════════════════════════════
  // ── FACTURATION PRESTATAIRE ──────────────────────────────
  // ══════════════════════════════════════════════════════════

  loadFactuSettings() {
    const stored = localStorage.getItem('rw_factu_' + this.config.event.id);
    if (stored) {
      const s = JSON.parse(stored);
      const fields = {
        factuBase: s.base, factuSeriPos: s.seriPos, factuCuisson: s.cuisson,
        factuDTF: s.dtf, factuAnimation: s.animation, factuCadresQty: s.cadresQty,
        factuCadresPU: s.cadresPU, factuAutres: s.autres, factuAutresLabel: s.autresLabel
      };
      Object.entries(fields).forEach(([id, val]) => {
        const el = document.getElementById(id);
        if (el && val !== undefined) el.value = val;
      });
    }
  }

  saveFactuSettings() {
    const s = {
      base: parseFloat(document.getElementById('factuBase')?.value) || 0,
      seriPos: parseFloat(document.getElementById('factuSeriPos')?.value) || 0,
      cuisson: parseFloat(document.getElementById('factuCuisson')?.value) || 0,
      dtf: parseFloat(document.getElementById('factuDTF')?.value) || 0,
      animation: parseFloat(document.getElementById('factuAnimation')?.value) || 0,
      cadresQty: parseInt(document.getElementById('factuCadresQty')?.value) || 0,
      cadresPU: parseFloat(document.getElementById('factuCadresPU')?.value) || 0,
      autres: parseFloat(document.getElementById('factuAutres')?.value) || 0,
      autresLabel: document.getElementById('factuAutresLabel')?.value || '',
    };
    localStorage.setItem('rw_factu_' + this.config.event.id, JSON.stringify(s));
  }

  resetFactuSettings() {
    localStorage.removeItem('rw_factu_' + this.config.event.id);
    const defaults = {
      factuBase: 9, factuSeriPos: 2.5, factuCuisson: 1, factuDTF: 1,
      factuAnimation: 340, factuCadresQty: 4, factuCadresPU: 40, factuAutres: 0, factuAutresLabel: ''
    };
    Object.entries(defaults).forEach(([id, val]) => {
      const el = document.getElementById(id);
      if (el) el.value = val;
    });
    this.renderFactu();
    this.toast('Prix réinitialisés');
  }

  _getFactuPricing() {
    return {
      base: parseFloat(document.getElementById('factuBase')?.value) || 0,
      seriPos: parseFloat(document.getElementById('factuSeriPos')?.value) || 0,
      cuisson: parseFloat(document.getElementById('factuCuisson')?.value) || 0,
      dtf: parseFloat(document.getElementById('factuDTF')?.value) || 0,
      animation: parseFloat(document.getElementById('factuAnimation')?.value) || 0,
      cadresQty: parseInt(document.getElementById('factuCadresQty')?.value) || 0,
      cadresPU: parseFloat(document.getElementById('factuCadresPU')?.value) || 0,
      autres: parseFloat(document.getElementById('factuAutres')?.value) || 0,
      autresLabel: document.getElementById('factuAutresLabel')?.value || '',
    };
  }

  _getFactuOrders() {
    const incPending = document.getElementById('factuPending')?.checked;
    const incProcessing = document.getElementById('factuProcessing')?.checked;
    const incCompleted = document.getElementById('factuCompleted')?.checked;
    const incCancelled = document.getElementById('factuCancelled')?.checked;
    const excludeTests = document.getElementById('factuExcludeTests')?.checked;

    return this.orders.filter(o => {
      if (excludeTests && this._isTestOrder(o)) return false;
      if (o.status === 'pending' && !incPending) return false;
      if (o.status === 'processing' && !incProcessing) return false;
      if (o.status === 'completed' && !incCompleted) return false;
      if (o.status === 'cancelled' && !incCancelled) return false;
      return true;
    });
  }

  _analyzeOrder(o) {
    const front = o.designs?.front_name || 'Aucun';
    const back = o.designs?.back_name || 'Aucun';
    const stampsText = o.designs?.stamps_text || 'Aucun';

    let seriPositions = 0;
    if (front !== 'Aucun') seriPositions++;
    if (back !== 'Aucun') seriPositions++;

    let stampCount = 0;
    if (stampsText !== 'Aucun') {
      stampCount = (stampsText.match(/Tampon/g) || []).length;
    }

    return { seriPositions, stampCount, hasSeri: seriPositions > 0 };
  }

  _calcOrderCost(o, pricing) {
    const a = this._analyzeOrder(o);
    const costBase = pricing.base;
    // Seri per position + cuisson forfait par piece (si au moins 1 position)
    const costSeri = a.seriPositions * pricing.seriPos + (a.hasSeri ? pricing.cuisson : 0);
    const costDTF = a.stampCount * pricing.dtf;
    return { ...a, costBase, costSeri, costDTF, costTotal: costBase + costSeri + costDTF };
  }

  renderFactu() {
    this.loadFactuSettings();
    const pricing = this._getFactuPricing();
    const orders = this._getFactuOrders();

    // Aggregate
    let totalBases = orders.length;
    let totalSeriPos = 0, totalPiecesWithSeri = 0, totalStamps = 0;
    let totalCostVar = 0, totalCAClient = 0;
    let rectoVerso = 0, rectoOnly = 0, noSeri = 0;

    const rows = orders.map(o => {
      const c = this._calcOrderCost(o, pricing);
      totalSeriPos += c.seriPositions;
      if (c.hasSeri) totalPiecesWithSeri++;
      totalStamps += c.stampCount;
      totalCostVar += c.costTotal;
      totalCAClient += (o.price || 0);
      if (c.seriPositions === 2) rectoVerso++;
      else if (c.seriPositions === 1) rectoOnly++;
      else noSeri++;
      return { order: o, cost: c };
    });

    const costBases = totalBases * pricing.base;
    const costSeri = totalSeriPos * pricing.seriPos;
    const costCuisson = totalPiecesWithSeri * pricing.cuisson;
    const costDTF = totalStamps * pricing.dtf;
    const costCadres = pricing.cadresQty * pricing.cadresPU;
    const totalFixed = pricing.animation + costCadres + pricing.autres;
    const totalAll = totalFixed + totalCostVar;
    const marge = totalCAClient - totalAll;
    const margePct = totalCAClient > 0 ? (marge / totalCAClient * 100) : 0;

    // Summary cards
    document.getElementById('factuSummary').innerHTML = `
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:10px;margin-bottom:12px">
        <div style="background:#e8f5e9;padding:14px;border-radius:10px">
          <div style="font-size:22px;font-weight:700">${totalCAClient.toFixed(0)} EUR</div>
          <div style="font-size:9px;letter-spacing:0.06em;color:#2e7d32;margin-top:2px">CA CLIENT</div>
        </div>
        <div style="background:#fff3e0;padding:14px;border-radius:10px">
          <div style="font-size:22px;font-weight:700">${totalAll.toFixed(2)} EUR</div>
          <div style="font-size:9px;letter-spacing:0.06em;color:#e65100;margin-top:2px">TOTAL PRESTATION</div>
        </div>
        <div style="background:${marge >= 0 ? '#e8f5e9' : '#ffebee'};padding:14px;border-radius:10px">
          <div style="font-size:22px;font-weight:700">${marge.toFixed(2)} EUR</div>
          <div style="font-size:9px;letter-spacing:0.06em;color:${marge >= 0 ? '#2e7d32' : '#c62828'};margin-top:2px">MARGE CLIENT (${margePct.toFixed(1)}%)</div>
        </div>
        <div style="background:var(--f5);padding:14px;border-radius:10px">
          <div style="font-size:22px;font-weight:700">${totalBases}</div>
          <div style="font-size:9px;letter-spacing:0.06em;color:var(--66);margin-top:2px">PIECES FACTUREES</div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
        <div style="background:var(--f5);border-radius:10px;padding:14px">
          <div style="font-size:11px;font-weight:700;letter-spacing:0.06em;margin-bottom:8px">FORFAITS FIXES</div>
          <div style="display:flex;justify-content:space-between;font-size:11px;padding:3px 0;border-bottom:1px solid #eee"><span>Animation sérigraphie</span><span style="font-weight:700">${pricing.animation.toFixed(2)} EUR</span></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;padding:3px 0;border-bottom:1px solid #eee"><span>Cadres A3+ (${pricing.cadresQty} x ${pricing.cadresPU.toFixed(0)} EUR)</span><span style="font-weight:700">${costCadres.toFixed(2)} EUR</span></div>
          ${pricing.autres > 0 ? '<div style="display:flex;justify-content:space-between;font-size:11px;padding:3px 0;border-bottom:1px solid #eee"><span>' + (pricing.autresLabel || 'Autres frais') + '</span><span style="font-weight:700">' + pricing.autres.toFixed(2) + ' EUR</span></div>' : ''}
          <div style="display:flex;justify-content:space-between;font-size:12px;padding:6px 0;font-weight:700"><span>TOTAL FORFAIT</span><span>${totalFixed.toFixed(2)} EUR</span></div>
        </div>
        <div style="background:var(--f5);border-radius:10px;padding:14px">
          <div style="font-size:11px;font-weight:700;letter-spacing:0.06em;margin-bottom:8px">COUTS VARIABLES</div>
          <div style="display:flex;justify-content:space-between;font-size:11px;padding:3px 0;border-bottom:1px solid #eee"><span>Bases (${totalBases} x ${pricing.base.toFixed(2)})</span><span style="font-weight:700">${costBases.toFixed(2)} EUR</span></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;padding:3px 0;border-bottom:1px solid #eee"><span>Sérigraphie (${totalSeriPos} pos x ${pricing.seriPos.toFixed(2)})</span><span style="font-weight:700">${costSeri.toFixed(2)} EUR</span></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;padding:3px 0;border-bottom:1px solid #eee"><span>Cuisson séri (${totalPiecesWithSeri} pcs x ${pricing.cuisson.toFixed(2)})</span><span style="font-weight:700">${costCuisson.toFixed(2)} EUR</span></div>
          <div style="display:flex;justify-content:space-between;font-size:11px;padding:3px 0;border-bottom:1px solid #eee"><span>DTF tampons (${totalStamps} x ${pricing.dtf.toFixed(2)})</span><span style="font-weight:700">${costDTF.toFixed(2)} EUR</span></div>
          <div style="display:flex;justify-content:space-between;font-size:12px;padding:6px 0;font-weight:700"><span>TOTAL VARIABLE</span><span>${totalCostVar.toFixed(2)} EUR</span></div>
        </div>
      </div>
      <div style="font-size:10px;color:var(--66);margin-top:8px">R/V: ${rectoVerso} pcs | Recto seul: ${rectoOnly} pcs | Sans seri: ${noSeri} pcs | DTF: ${totalStamps} tampons sur ${rows.filter(r => r.cost.stampCount > 0).length} pcs</div>
    `;

    // Detail table
    const statusLabels = { pending: 'Attente', processing: 'Prep', completed: 'Terminée', cancelled: 'Annulée' };
    let html = `<table style="width:100%;border-collapse:collapse;font-size:10px">
      <thead><tr style="border-bottom:2px solid #000;text-align:left">
        <th style="padding:6px 4px">#</th>
        <th style="padding:6px 4px">ID</th>
        <th style="padding:6px 4px">CLIENT</th>
        <th style="padding:6px 4px">TAILLE</th>
        <th style="padding:6px 4px">SERI POS</th>
        <th style="padding:6px 4px">DTF</th>
        <th style="padding:6px 4px;text-align:right">BASE</th>
        <th style="padding:6px 4px;text-align:right">SERI</th>
        <th style="padding:6px 4px;text-align:right">DTF</th>
        <th style="padding:6px 4px;text-align:right;font-weight:700">COUT PRESTA</th>
        <th style="padding:6px 4px;text-align:right">PV CLIENT</th>
        <th style="padding:6px 4px">STATUT</th>
      </tr></thead><tbody>`;

    rows.forEach((r, i) => {
      const o = r.order;
      const c = r.cost;
      const seriLabel = c.seriPositions === 2 ? 'R+V' : c.seriPositions === 1 ? 'R' : '-';
      html += `<tr style="border-bottom:1px solid #eee">
        <td style="padding:5px 4px">${i+1}</td>
        <td style="padding:5px 4px;font-weight:700">${o.id}</td>
        <td style="padding:5px 4px">${o.customer?.name || ''}</td>
        <td style="padding:5px 4px;text-align:center">${o.customer?.size || ''}</td>
        <td style="padding:5px 4px;text-align:center">${seriLabel}</td>
        <td style="padding:5px 4px;text-align:center">${c.stampCount || '-'}</td>
        <td style="padding:5px 4px;text-align:right">${c.costBase.toFixed(2)}</td>
        <td style="padding:5px 4px;text-align:right">${c.costSeri.toFixed(2)}</td>
        <td style="padding:5px 4px;text-align:right">${c.costDTF.toFixed(2)}</td>
        <td style="padding:5px 4px;text-align:right;font-weight:700">${c.costTotal.toFixed(2)}</td>
        <td style="padding:5px 4px;text-align:right">${(o.price || 0).toFixed(0)}</td>
        <td style="padding:5px 4px"><span class="bdg bdg-${o.status}">${statusLabels[o.status] || o.status}</span></td>
      </tr>`;
    });

    html += `</tbody><tfoot>
      <tr style="border-top:2px solid #000;font-weight:700;font-size:11px">
        <td colspan="6" style="padding:8px 4px">TOTAL VARIABLE (${totalBases} pieces)</td>
        <td style="padding:8px 4px;text-align:right">${costBases.toFixed(2)}</td>
        <td style="padding:8px 4px;text-align:right">${(costSeri + costCuisson).toFixed(2)}</td>
        <td style="padding:8px 4px;text-align:right">${costDTF.toFixed(2)}</td>
        <td style="padding:8px 4px;text-align:right">${totalCostVar.toFixed(2)}</td>
        <td style="padding:8px 4px;text-align:right">${totalCAClient.toFixed(0)}</td>
        <td></td>
      </tr>
      <tr style="font-weight:700;font-size:11px;color:var(--66)">
        <td colspan="9" style="padding:4px">+ FORFAIT FIXE</td>
        <td style="padding:4px;text-align:right">${totalFixed.toFixed(2)}</td>
        <td colspan="2"></td>
      </tr>
      <tr style="font-weight:700;font-size:13px;border-top:1px solid #000">
        <td colspan="9" style="padding:8px 4px">TOTAL PRESTATION</td>
        <td style="padding:8px 4px;text-align:right">${totalAll.toFixed(2)}</td>
        <td colspan="2"></td>
      </tr>
    </tfoot></table>`;

    document.getElementById('factuTable').innerHTML = html;
  }

  // ── FACTU PDF ───────────────────────────────────────────
  exportFactuPDF() {
    const pricing = this._getFactuPricing();
    const orders = this._getFactuOrders();
    if (!orders.length) { this.toast('Aucune commande'); return; }

    let totalBases = orders.length, totalSeriPos = 0, totalPiecesWithSeri = 0, totalStamps = 0, totalCostVar = 0, totalCA = 0;
    let rectoVerso = 0, rectoOnly = 0;
    const tableRows = orders.map((o, i) => {
      const c = this._calcOrderCost(o, pricing);
      totalSeriPos += c.seriPositions;
      if (c.hasSeri) totalPiecesWithSeri++;
      totalStamps += c.stampCount;
      totalCostVar += c.costTotal;
      totalCA += (o.price || 0);
      if (c.seriPositions === 2) rectoVerso++;
      else if (c.seriPositions === 1) rectoOnly++;
      const seriLabel = c.seriPositions === 2 ? 'R+V' : c.seriPositions === 1 ? 'R' : '-';
      const d = new Date(o.timestamp);
      return `<tr><td class="ac">${i+1}</td><td class="bld">${o.id}</td><td>${d.toLocaleDateString('fr-FR')}</td><td>${o.customer?.name||''}</td><td class="ac">${o.customer?.size||''}</td><td class="ac">${seriLabel}</td><td class="ac">${c.stampCount||'-'}</td><td class="ar">${c.costBase.toFixed(2)}</td><td class="ar">${c.costSeri.toFixed(2)}</td><td class="ar">${c.costDTF.toFixed(2)}</td><td class="ar bld">${c.costTotal.toFixed(2)}</td></tr>`;
    }).join('');

    const costBases = totalBases * pricing.base;
    const costSeri = totalSeriPos * pricing.seriPos;
    const costCuisson = totalPiecesWithSeri * pricing.cuisson;
    const costDTF = totalStamps * pricing.dtf;
    const costCadres = pricing.cadresQty * pricing.cadresPU;
    const totalFixed = pricing.animation + costCadres + pricing.autres;
    const totalAll = totalFixed + totalCostVar;
    const marge = totalCA - totalAll;

    const eventName = this.config.event.name || 'Event';
    const eventDate = this.config.event.date_display || '';

    const body = `
      <div class="rw-infobar">
        <div class="ib-item"><strong>Event :</strong> ${eventName}</div>
        <div class="ib-item"><strong>Date :</strong> ${eventDate}</div>
        <div class="ib-item"><strong>Lieu :</strong> Lyon</div>
        <div class="ib-item"><strong>Pieces :</strong> ${totalBases}</div>
      </div>

      <div class="rw-section">
        <div class="rw-section-num">1. Forfait animation + cadres</div>
        <div class="rw-recap">
          <div class="rw-recap-row"><span>Animation sérigraphie live &mdash; Journée complète</span><span>${pricing.animation.toFixed(2)} &euro;</span></div>
          <div class="rw-recap-row"><span>Cadres A3+ (${pricing.cadresQty} cadres &times; ${pricing.cadresPU.toFixed(2)} &euro;)</span><span>${costCadres.toFixed(2)} &euro;</span></div>
          ${pricing.autres > 0 ? '<div class="rw-recap-row"><span>' + (pricing.autresLabel || 'Autres frais') + '</span><span>' + pricing.autres.toFixed(2) + ' &euro;</span></div>' : ''}
          <div class="rw-recap-row sub"><span>Direction artistique + logistique (Lyon)</span><span>Offert</span></div>
          <div class="rw-recap-row total"><span>Sous-total forfait</span><span>${totalFixed.toFixed(2)} &euro;</span></div>
        </div>
      </div>

      <div class="rw-section">
        <div class="rw-section-num">2. Impression sur place &mdash; Facturation post-événement</div>
        <div class="rw-note">
          Chaque T-shirt imprimé pendant l'événement est facturé à l'unité. Le prix dépend du nombre de positions imprimées (recto seul ou recto + verso) et de la technique choisie.
        </div>
        <div class="rw-cols">
          <div>
            <div class="rw-recap">
              <div class="rw-recap-row sub"><span>Impression sérigraphie (par position)</span><span>${pricing.seriPos.toFixed(2)} &euro; / pos</span></div>
              <div class="rw-recap-row sub"><span>Cuisson / fixation (par piece)</span><span>${pricing.cuisson.toFixed(2)} &euro; / pos</span></div>
              <div class="rw-recap-row sub"><span>DTF tampon (pose + cuisson)</span><span>${pricing.dtf.toFixed(2)} &euro; / tampon</span></div>
              <div class="rw-recap-row sub"><span>Base T-shirt RWRKD Blanks</span><span>${pricing.base.toFixed(2)} &euro; / piece</span></div>
            </div>
          </div>
          <div>
            <div class="rw-recap">
              <div class="rw-recap-row"><span>Bases RWRKD Blanks (${totalBases} pcs)</span><span>${costBases.toFixed(2)} &euro;</span></div>
              <div class="rw-recap-row"><span>Sérigraphie (${totalSeriPos} positions)</span><span>${costSeri.toFixed(2)} &euro;</span></div>
              <div class="rw-recap-row"><span>Cuisson / fixation (${totalPiecesWithSeri} pcs)</span><span>${costCuisson.toFixed(2)} &euro;</span></div>
              <div class="rw-recap-row"><span>DTF tampons (${totalStamps} tampons)</span><span>${costDTF.toFixed(2)} &euro;</span></div>
              <div class="rw-recap-row total"><span>Sous-total variable</span><span>${totalCostVar.toFixed(2)} &euro;</span></div>
            </div>
          </div>
        </div>
        <div style="font-size:8px;color:#666;margin-bottom:12px">R+V : ${rectoVerso} pcs &nbsp; | &nbsp; Recto seul : ${rectoOnly} pcs &nbsp; | &nbsp; Sans seri : ${totalBases - totalPiecesWithSeri} pcs &nbsp; | &nbsp; DTF : ${totalStamps} tampons sur ${orders.filter(o => (this._analyzeOrder(o).stampCount > 0)).length} pcs</div>
      </div>

      <div class="rw-section">
        <div class="rw-section-num">Récapitulatif</div>
        <div class="rw-recap">
          <div class="rw-recap-row"><span>Forfait animation + cadres</span><span>${totalFixed.toFixed(2)} &euro;</span></div>
          <div class="rw-recap-row"><span>Impressions + bases (${totalBases} pieces)</span><span>${totalCostVar.toFixed(2)} &euro;</span></div>
          <div class="rw-recap-row grand"><span>TOTAL PRESTATION</span><span>${totalAll.toFixed(2)} &euro;</span></div>
        </div>
        <div style="display:flex;gap:16px;margin-top:10px">
          <div class="rw-stat" style="flex:1"><div class="rw-stat-val">${totalCA.toFixed(0)} &euro;</div><div class="rw-stat-label">CA encaisse par le client</div></div>
          <div class="rw-stat" style="flex:1"><div class="rw-stat-val">${totalAll.toFixed(2)} &euro;</div><div class="rw-stat-label">Total prestation REWORKED</div></div>
          <div class="rw-stat" style="flex:1;${marge >= 0 ? '' : 'border-color:#c62828'}"><div class="rw-stat-val" style="${marge < 0 ? 'color:#c62828' : ''}">${marge.toFixed(2)} &euro;</div><div class="rw-stat-label">Marge client (${totalCA > 0 ? (marge/totalCA*100).toFixed(1) : 0}%)</div></div>
        </div>
      </div>

      <div class="rw-section">
        <div class="rw-section-num">3. Détail par commande (${orders.length} pieces)</div>
        <table>
          <thead><tr><th>#</th><th>ID</th><th>Date</th><th>Client</th><th>Taille</th><th>Seri</th><th>DTF</th><th class="ar">Base</th><th class="ar">Seri</th><th class="ar">DTF</th><th class="ar">Total</th></tr></thead>
          <tbody>${tableRows}</tbody>
          <tfoot>
            <tr style="border-top:2px solid #000"><td colspan="7">TOTAL VARIABLE</td><td class="ar">${costBases.toFixed(2)}</td><td class="ar">${(costSeri+costCuisson).toFixed(2)}</td><td class="ar">${costDTF.toFixed(2)}</td><td class="ar">${totalCostVar.toFixed(2)}</td></tr>
            <tr style="background:#f7f7f7"><td colspan="9">+ FORFAIT FIXE</td><td></td><td class="ar">${totalFixed.toFixed(2)}</td></tr>
            <tr style="background:#000;color:#fff;font-size:11px"><td colspan="9">TOTAL PRESTATION</td><td></td><td class="ar">${totalAll.toFixed(2)}</td></tr>
          </tfoot>
        </table>
      </div>`;

    this._pdfOpen('Facturation ' + eventName, 'FACTURATION POST-EVENEMENT', eventName, body);
  }

  // ── FACTU CSV ───────────────────────────────────────────
  exportFactuCSV() {
    const pricing = this._getFactuPricing();
    const orders = this._getFactuOrders();
    if (!orders.length) { this.toast('Aucune commande'); return; }

    const headers = ['#','ID','Date','Client','Taille','Base','Design AV','Design AR','Tampons','Seri Positions','DTF Qty','Cout Base','Cout Seri','Cout DTF','Cout Total','PV Client','Statut'];
    const statusLabels = { pending: 'En attente', processing: 'En préparation', completed: 'Terminée', cancelled: 'Annulée' };

    const csvRows = orders.map((o, i) => {
      const c = this._calcOrderCost(o, pricing);
      const d = new Date(o.timestamp);
      return [i+1, o.id, d.toLocaleDateString('fr-FR'), o.customer?.name||'', o.customer?.size||'',
        (o.designs?.base_name||'').replace('T-shirt ',''), o.designs?.front_name||'Aucun', o.designs?.back_name||'Aucun',
        o.designs?.stamps_text||'Aucun', c.seriPositions, c.stampCount,
        c.costBase.toFixed(2), c.costSeri.toFixed(2), c.costDTF.toFixed(2), c.costTotal.toFixed(2),
        (o.price||0).toFixed(0), statusLabels[o.status]||o.status];
    });

    const esc = v => { const s = String(v); return s.includes(',') || s.includes('"') || s.includes('\n') ? '"' + s.replace(/"/g,'""') + '"' : s; };
    const csv = '\uFEFF' + [headers.map(esc).join(','), ...csvRows.map(r => r.map(esc).join(','))].join('\n');

    this._downloadFile(csv, 'text/csv;charset=utf-8', `facturation-${this.config.event.id}-${this._dateSlug()}.csv`);
    this.toast('CSV facturation téléchargé');
  }

  // ── FACTU CLIPBOARD ─────────────────────────────────────
  exportFactuClipboard() {
    const pricing = this._getFactuPricing();
    const orders = this._getFactuOrders();
    if (!orders.length) { this.toast('Aucune commande'); return; }

    const headers = ['#','ID','Date','Client','Taille','Seri Pos','DTF Qty','Cout Base','Cout Seri','Cout DTF','Cout Total','PV Client','Statut'];
    const statusLabels = { pending: 'Attente', processing: 'Prep', completed: 'Terminée', cancelled: 'Annulée' };

    const tsvRows = orders.map((o, i) => {
      const c = this._calcOrderCost(o, pricing);
      const d = new Date(o.timestamp);
      return [i+1, o.id, d.toLocaleDateString('fr-FR'), o.customer?.name||'', o.customer?.size||'',
        c.seriPositions, c.stampCount, c.costBase.toFixed(2), c.costSeri.toFixed(2), c.costDTF.toFixed(2),
        c.costTotal.toFixed(2), (o.price||0).toFixed(0), statusLabels[o.status]||o.status].join('\t');
    });

    const tsv = [headers.join('\t'), ...tsvRows].join('\n');
    navigator.clipboard.writeText(tsv).then(() => {
      this.toast('Copié ! Colle dans Google Sheets');
    }).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = tsv; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
      this.toast('Copié ! Colle dans Google Sheets');
    });
  }

  // ── RENDER STAMPS ON DASHBOARD ORDER PHOTOS ──────────────
  renderDashStamps() {
    const assetBase = this.configUrl.substring(0, this.configUrl.lastIndexOf('/') + 1);
    const stampConfigs = this.config?.stamps || [];

    this.orders.forEach(o => {
      const stamps = o.designs?.stamps_data || [];
      if (!stamps.length) return;

      const frontBox = document.querySelector(`[data-order-front="${o.draft_id}"]`);
      const backBox = document.querySelector(`[data-order-back="${o.draft_id}"]`);
      const prepFront = document.querySelector(`[data-prep-front="${o.draft_id}"]`);
      const prepBack = document.querySelector(`[data-prep-back="${o.draft_id}"]`);

      [frontBox, backBox, prepFront, prepBack].forEach(box => {
        if (box) box.querySelectorAll('.dash-stamp').forEach(el => el.remove());
      });

      stamps.forEach(s => {
        const sc = stampConfigs.find(st => st.id === s.id);
        if (!sc) return;
        const isFront = (s.face || 'front') === 'front';
        const box = isFront ? frontBox : backBox;
        const prepBox = isFront ? prepFront : prepBack;
        const pct = (sc.size_cm || 5) * 1.1;
        const file = this.getStampFile(sc.image, s.color);

        [box, prepBox].forEach(b => {
          if (!b) return;
          const el = document.createElement('div');
          el.className = 'dash-stamp';
          el.style.cssText = `position:absolute;left:${s.px || 50}%;top:${s.py || 50}%;transform:translate(-50%,-50%);width:${pct}%;min-width:16px;pointer-events:none;z-index:5`;
          el.innerHTML = `<img src="${assetBase}${file}?v=20260520" style="width:100%;height:auto">`;
          b.appendChild(el);
        });
      });
    });
  }

  // ── STAMP FILE HELPER ────────────────────────────────────
  getStampFile(img, color) {
    if (!color || color === 'noir') return img;
    return img.replace('.svg', '-' + ({blanc:'white',bleu:'blue',rouge:'red'}[color] || color) + '.svg');
  }

  // ── FULL PRODUCT VIEW (both mockups + stamps) ───────────
  showFullView(order) {
    const assetBase = this.configUrl.substring(0, this.configUrl.lastIndexOf('/') + 1);
    const frontUrl = this.getMockupUrl('front', order.designs?.base_name, order.designs?.front_name);
    const backUrl = this.getMockupUrl('back', order.designs?.base_name, order.designs?.back_name);
    const stamps = order.designs?.stamps_data || [];

    // Build stamp overlays HTML
    const stampConfigs = this.config?.stamps || [];
    const stampOverlays = stamps.map(s => {
      const sc = stampConfigs.find(st => st.id === s.id);
      if (!sc) return '';
      const pct = (sc.size_cm || 5) * 0.55;
      const file = this.getStampFile(sc.image, s.color);
      return `<div style="position:absolute;left:${s.x}%;top:${s.y}%;transform:translate(-50%,-50%);width:${pct}%;min-width:30px;pointer-events:none"><img src="${assetBase}${file}?v=20260520" style="width:100%;height:auto"></div>`;
    }).join('');

    let m = document.getElementById('dashFullView');
    if (!m) { m = document.createElement('div'); m.id = 'dashFullView'; m.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.95);z-index:9999;display:flex;align-items:center;justify-content:center;gap:16px;padding:20px;cursor:pointer;flex-wrap:wrap'; m.onclick = (e) => { if (e.target === m) m.style.display = 'none'; }; document.body.appendChild(m); }

    // Build stamp overlays for each face
    const buildStampOverlays = (face) => {
      return stamps.filter(s => (s.face || 'front') === face).map(s => {
        const sc = stampConfigs.find(st => st.id === s.id);
        if (!sc) return '';
        const pct = (sc.size_cm || 5) * 1.1;
        const file = this.getStampFile(sc.image, s.color);
        return `<div style="position:absolute;left:${s.px || 50}%;top:${s.py || 50}%;transform:translate(-50%,-50%);width:${pct}%;pointer-events:none"><img src="${assetBase}${file}?v=20260520" style="width:100%;height:auto"></div>`;
      }).join('');
    };

    m.innerHTML = `
      <div style="position:absolute;top:16px;right:20px;color:#fff;font-size:28px;cursor:pointer" onclick="this.parentElement.style.display='none'">&times;</div>
      <div style="position:absolute;top:16px;left:20px;color:#fff;font-size:12px;font-weight:700;letter-spacing:0.08em">${order.id} - ${order.customer?.name || ''} - Taille ${order.customer?.size || ''}</div>
      <div style="position:relative;max-width:44vw;max-height:85vh">
        <img src="${frontUrl}" style="width:100%;max-height:85vh;object-fit:contain;border-radius:10px">
        ${buildStampOverlays('front')}
        <div style="position:absolute;bottom:8px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.7);color:#fff;padding:4px 12px;border-radius:20px;font-size:10px;font-weight:700;letter-spacing:0.08em">AVANT</div>
      </div>
      <div style="position:relative;max-width:44vw;max-height:85vh">
        <img src="${backUrl}" style="width:100%;max-height:85vh;object-fit:contain;border-radius:10px">
        ${buildStampOverlays('back')}
        <div style="position:absolute;bottom:8px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.7);color:#fff;padding:4px 12px;border-radius:20px;font-size:10px;font-weight:700;letter-spacing:0.08em">ARRIERE</div>
      </div>
      ${stamps.length ? '<div style="position:absolute;bottom:16px;left:50%;transform:translateX(-50%);background:rgba(255,243,205,0.95);color:#000;padding:8px 16px;border-radius:8px;font-size:11px;font-weight:600">' + order.designs?.stamps_text + '</div>' : ''}
    `;
    m.style.display = 'flex';
  }

  // ── ZOOM (clone the entire design box including stamp overlays) ──
  zoomBox(boxEl) {
    if (!boxEl) return;
    let m = document.getElementById('dashZoom');
    if (!m) { m = document.createElement('div'); m.id = 'dashZoom'; m.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.9);z-index:9999;display:flex;align-items:center;justify-content:center;padding:20px'; m.onclick = (e) => { if (e.target === m) m.style.display = 'none'; }; document.body.appendChild(m); }
    // Deep clone the box
    const clone = boxEl.cloneNode(true);
    clone.style.cssText = 'position:relative;max-width:80vw;max-height:85vh;background:#fff;border-radius:12px;padding:12px;overflow:visible';
    // Remove zoom button from clone
    clone.querySelectorAll('.order-zoom').forEach(z => z.remove());
    // Make image large
    const img = clone.querySelector('img');
    if (img) img.style.cssText = 'width:100%;max-height:80vh;object-fit:contain;border-radius:8px;display:block';
    // Scale up stamp overlays
    clone.querySelectorAll('.dash-stamp').forEach(s => {
      const curW = parseInt(s.style.width) || 20;
      s.style.width = Math.max(curW * 3, 40) + 'px';
      s.style.minWidth = '40px';
    });
    m.innerHTML = '<div style="position:absolute;top:16px;right:20px;color:#fff;font-size:28px;cursor:pointer;z-index:10" onclick="this.parentElement.style.display=\'none\'">&times;</div>';
    m.appendChild(clone);
    m.style.display = 'flex';
  }

  // ── MOCKUP URL HELPER ────────────────────────────────────
  getMockupUrl(face, baseName, designName) {
    const assetBase = this.configUrl.substring(0, this.configUrl.lastIndexOf('/') + 1);
    const isBlack = baseName && baseName.toLowerCase().includes('noir');
    const b = isBlack ? 'black' : 'white';
    let color = null;
    if (designName && designName.toLowerCase().includes('bleu')) color = 'blue';
    if (designName && designName.toLowerCase().includes('rouge')) color = 'red';
    const file = color ? `mockup-${face}-${b}-${color}.jpg` : `mockup-${face}-${b}-blank.jpg`;
    return assetBase + file + '?v=20260520';
  }

  formatTime(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1) return 'A l\'instant';
    if (diff < 60) return 'Il y a ' + diff + ' min';
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }

  showNotif(msg) {
    const el = document.getElementById('newOrderNotif');
    el.textContent = msg;
    el.classList.add('on');
    el.onclick = () => el.classList.remove('on');
    setTimeout(() => el.classList.remove('on'), 4000);
  }

  toast(msg) {
    const container = document.getElementById('toastContainer');
    const el = document.createElement('div');
    el.className = 'toast';
    el.textContent = msg;
    container.appendChild(el);
    setTimeout(() => {
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 200);
    }, 3000);
  }
}
